import React from "react";

import { Title } from "~/components";
import { env } from "~/env";
import { usePageTitle } from "~/shared";

export const PatientAgreement = () => {
  usePageTitle("Patient Agreement");

  return (
    <main className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 text-center">
          <Title className="mb-2 text-2xl font-bold text-gray-900 md:text-3xl">
            Elektra Health Patient Services Agreement
          </Title>
          <p className="text-sm text-gray-500">
            <strong>Last modified:</strong> December 11, 2025
          </p>
        </div>

        <div className="space-y-4">
          {/* Introduction Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Introduction
            </h2>
            <p className="mb-4 text-gray-700">
              My Elektra, Inc. ("Elektra Health"), through its partnerships with
              professional services corporations, including Electra Health
              Medical Mass PC, Electra Health Medical, P.C., Elektra Women's
              Health Medical, Inc., and Electra Women's Health Medical NJ, PC
              (d/b/a Elektra Health Medical Services), is honored to provide you
              with personalized support and virtual care. Elektra Health
              provides management and administrative services to Elektra Health
              Medical Services; Elektra Health Medical Services is solely
              responsible for the delivery of medical and other licensed
              professional services (collectively, Elektra Health and Elektra
              Health Medical Services are referred to as "us", "we", "our", or
              "them").
            </p>
            <p className="mb-4 text-gray-700">
              Although this document is long, it is very important that you
              understand it. When you sign this document, it will represent an
              agreement between us. Federal and state law requires that we
              provide to you certain information set forth in this document. A
              full copy of this Patient Service Agreement and Consent Form will
              be furnished for your records upon request. If you have any
              questions, please ask your provider or our office.
            </p>
          </section>

          {/* 1. Scheduling Services */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              1. Scheduling Services
            </h2>
            <p className="text-gray-700">
              All services can be scheduled by visiting our website or logging
              into the Elektra Health secure patient portal or electronic
              medical record ("patient portal").
            </p>
          </section>

          {/* 2. Payment */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              2. Payment
            </h2>
            <p className="mb-4 text-gray-700">
              In addition to the foregoing authorization for assignment of
              benefits, by agreeing to use the Services, you acknowledge and
              agree that, to the extent the Services you use are NOT a "Covered
              Service" (as defined by your Health Plan)("Non-Covered Services")
              (i) you are explicitly choosing to obtain products and services on
              a cash basis outside of any federal or state healthcare program
              and you have sole financial responsibility for all Non-Covered
              Services provided to you, and (2) neither you nor Elektra Health
              nor the Practice will submit a claim for reimbursement to any
              federal or state healthcare program for the costs of such services
              and products provided to you through the Non-Covered Services.
            </p>
            <p className="mb-4 text-gray-700">
              Subject to any ability to refund as outlined herein, you agree
              that you will be charged for the Non-Covered Services, by
              providing a credit card or other payment method accepted by
              Elektra Health ("Payment Method"), and you are expressly agreeing
              that Elektra Health is authorized to charge to the Payment Method
              any fees for Non-Covered Services, together with any applicable
              taxes.
            </p>
            <p className="mb-4 text-gray-700">
              You agree that authorizations to charge your Payment Method remain
              in effect until you cancel it in writing by notification to
              Elektra Health at billing@elektrahealth.com, and you agree to
              notify Elektra Health of any changes to your Payment Method. You
              certify that you are an authorized user of the Payment Method and
              will not dispute charges for the Non-Covered Services. You
              acknowledge that the origination of ACH transactions to your
              account must comply with applicable provisions of U.S. law. In the
              case of an ACH transaction rejected for insufficient funds,
              Elektra Health may at its discretion attempt to process the charge
              again at any time within 30 days.
            </p>
            <p className="text-gray-700">
              Your payment for Telehealth Services and any other service you
              receive from Elektra Health Medical Services shall be made prior
              to or at the time of service, except for the portion of the
              payment that may be covered by your insurance plan. If you will be
              using insurance to cover some or all of the cost of your
              appointment, you should contact us ahead of your appointment to
              ensure that your insurance is accepted and provide a photo of your
              insurance card prior to the appointment via our patient intake or
              patient portal. You should be prepared to pay any co-payments
              prior to or at the time of the appointment. If we are
              out-of-network for your insurance, we may submit an out-of-network
              claim on your behalf and bill you only for the copayment,
              coinsurance, or amount not covered. We accept payment in the form
              of a credit card, which you expressly authorize for us to charge
              for the Telehealth Services and any other service you elect to
              receive from Elektra Health Medical Services that is not covered
              by your insurance.
            </p>
          </section>

          {/* 3. Cancellation Policy */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              3. Cancellation Policy
            </h2>
            <p className="text-gray-700">
              For any appointment that is scheduled in advance, excluding
              on-demand chat-based care interactions, you understand that your
              appointment must be canceled no less than twenty four [24] hours
              in advance or you will be responsible for a $
              {env.VITE_LATE_CANCELLATION_FEE} fee, unless your insurance
              prohibits us from billing you for missed visits.
            </p>
          </section>

          {/* 4. Consent for Assignment of Insurance Benefits */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              4. Consent for Assignment of Insurance Benefits
            </h2>
            <p className="mb-4 text-gray-700">
              You authorize the payment of insurance benefits directly to us,
              and you assign and transfer to us all right, title, and interest
              in the right to receive all benefits payable for the health care
              rendered that are provided for in any and all insurance policies
              and/or plans that will be applied to the amount charged for
              services rendered by your Elektra Health Medical Services
              provider(s). If Elektra bills your insurance company, other
              third-party payer, or a self-insured employer, you may be liable
              for any amounts not paid, whether the insurance company,
              third-party payer, or self-insured employer makes partial payment
              or declines to pay to Elektra.
            </p>
            <p className="mb-3 text-gray-700">
              You further authorize and irrevocably assign to Elektra Health the
              following rights:
            </p>
            <ol className="mb-4 ml-6 list-[lower-alpha] space-y-2 text-gray-700">
              <li className="pl-2">
                To communicate with your Health Plan, to request any adjustment
                to your Health Plan's reimbursement of the Services provided,
                and to file any and all necessary claims, demands or appeals
                with your Health Plan arising from a denied, underpaid or
                misclassified claim;
              </li>
              <li className="pl-2">
                To demand and receive the production of, or access to, any
                documents and information, including, without limitation, any
                copies of Health Plan documents, coverage policies, guidelines
                and any other materials affecting the coverage and reimbursement
                of any Services provided to you, from any entity or person to
                the fullest extent of your rights to do so under applicable law;
              </li>
              <li className="pl-2">
                To bring legal action, if needed, in any forum against your
                Health Plan under applicable laws, including, without
                limitation, the Employee Retirement Income Security Act of 1974,
                as amended ("ERISA") and/or the Federal Employee Health Benefit
                Act, as amended;
              </li>
              <li className="pl-2">
                To recover benefits under the terms of your Health Plan, to
                enforce your rights under the terms of your Health Plan, or to
                clarify your rights to future benefits under the terms of your
                Health Plan;
              </li>
              <li className="pl-2">
                To enjoin any act or practice which violates any provision of
                ERISA or the terms of your Health Plan, or to obtain other
                appropriate equitable relief to redress such violations or to
                enforce any provisions of ERISA or the terms of your Health
                Plan; and
              </li>
              <li className="pl-2">
                To recover the costs of pursuing such action, including,
                reasonable attorney fees, as permitted.
              </li>
              <li className="pl-2">
                The foregoing designation and assignment of benefits and rights
                are without limitation and without reservation of any part or
                aspect thereof.
              </li>
            </ol>
          </section>

          {/* 5. Telehealth */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              5. Telehealth
            </h2>
            <p className="mb-4 text-gray-700">
              You agree to receive telehealth services (the “Telehealth
              Services”). Telehealth involves the use of audio, video, or other
              electronic communications to interact with you, consult with your
              health care provider or Elektra Coach/Guide, and/or review your
              medical information for purposes of diagnosis, therapy, follow-up,
              and/or education. Telehealth may be provided synchronously (in
              real time) or asynchronously (not in real time, such as by sending
              a message or photo and later receiving a response). During your
              telehealth consultation, details of your medical history and
              personal health information may be discussed with other health
              professionals through secure video, audio, or telecommunications
              technology.
            </p>
            <p className="mb-4 text-gray-700">
              The benefits of telehealth include increased access to care,
              specialists, and health education without the need to travel
              outside of your home or local community. You understand that
              telehealth has limitations and may not be appropriate for all
              medical conditions. Certain medical services, treatments, and
              prescriptions cannot be provided via telehealth, and some
              medications, including controlled substances or drugs regulated by
              state or federal law, may require an in-person evaluation or may
              not be prescribed through telehealth. Telehealth does not allow
              for physical examinations or diagnostic testing that may be
              necessary for certain conditions, and your provider will
              determine, in their professional judgment, whether telehealth is
              clinically appropriate for your care. Because of your condition or
              due to technical issues, a face-to-face visit may be required
              before or after a telehealth encounter. Technical failures or
              delays may occur, and in rare circumstances, security protocols
              could fail, resulting in a breach of patient privacy.
            </p>
            <p className="mb-4 text-gray-700">
              The alternative to Telehealth Services is a face-to-face visit
              with an independent provider unaffiliated with Elektra. You
              understand the risks, benefits, and alternatives to Telehealth
              Services and may ask your provider any questions you have. You may
              be required to sign additional consents or provide additional
              information if required by the laws of the state in which you
              reside.
            </p>
            <p className="font-semibold text-gray-900">
              Telehealth Services are intended for non-emergency care only and
              should not be used for medical or mental health emergencies. In an
              emergency, you should dial 911 immediately, or if you are feeling
              suicidal or experiencing emotional distress, you should call or
              chat with the 988 Suicide & Crisis Lifeline.
            </p>
          </section>

          {/* 6. Integrated Approach to Care */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              6. Integrated Approach to Care in Regards to Data Sharing
            </h2>
            <p className="text-gray-700">
              Elektra Health Medical Services believes that the best care is
              provided when all members of your healthcare team work together in
              an integrated system of care. Accordingly, and in furtherance of
              this model, all professionals involved in your care, including
              primary care providers and Elektra Guides, may share information
              regarding your care and treatment in order to provide you with the
              best care possible for you, except when sharing this information
              is expressly prohibited by law. In addition, we sometimes work
              with third-parties who provide services on our behalf, such as
              help with coordinating your care. These third-parties may be
              considered business associates of ours and we will share your
              information with them as described in our{" "}
              <a
                href="https://www.elektrahealth.com/notice-of-privacy-practices/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black underline"
              >
                Notice of Privacy Practices
              </a>
              .
            </p>
          </section>

          {/* 7. Elektra Health Online Questionnaire */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              7. Elektra Health Online Questionnaire
            </h2>
            <p className="text-gray-700">
              You agree to: (i) fully, accurately and truthfully complete your
              Elektra Health Online Questionnaire or Intake Form; and (ii)
              prohibit anyone else from using your Elektra Health account
              employing information from your Elektra Health Online
              Questionnaire. You agree to provide accurate, current and complete
              information about yourself for your Elektra Health Online
              Questionnaire, and to periodically review and to update such
              information as needed to keep it accurate, current and complete.
              You agree to immediately notify Elektra Health of any actual or
              suspected unauthorized use of your Elektra Health account, profile
              or credentials or other security concerns of which you become
              aware.
            </p>
          </section>

          {/* 8. Relationship with Practitioner */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              8. Relationship with Practitioner
            </h2>
            <p className="text-gray-700">
              If you utilize telehealth services, you agree that you are
              entering into an agreement with Electra Health Medical, P.C., a
              New York professional service corporation, Electra Health Medical
              Mass, P.C., a Massachusetts professional service corporation,
              Elektra Women's Health Medical, Inc., a Pennsylvania professional
              corporation, and/or Electra Women's Health Medical NJ, PC, a New
              Jersey professional service corporation (collectively, the
              "Practice") which shall be a provider of professional medical
              services to you, which means, among other things, you are entering
              into a practitioner – patient relationship with the physician or
              licensed practitioner associated with the Practice (collectively,
              the "Practitioners") that personally performs the Services. You
              understand and agree that Elektra Health is the provider of
              certain administrative services to the Practice and provides
              access to a technology platform in order to facilitate the
              Services between you and the Practice and the Practitioners. You
              acknowledge that Elektra Health does not provide professional
              medical services itself and the Elektra Health website is for
              informational purposes only. Elektra Health does not provide
              medical advice, diagnosis, or treatment.
            </p>
          </section>

          {/* 9. Confidentiality and Compliance */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              9. Confidentiality and Compliance
            </h2>
            <p className="mb-4 text-gray-700">
              We will take appropriate precautions to keep your health
              information confidential. You are also protected under the
              provisions of the federal Health Insurance Portability and
              Accountability Act of 1996 ("HIPAA") and any other applicable
              federal and state laws related to the protection of patient
              information and how we will use and disclose your protected health
              information. Our Notice of Privacy Practices ("NPP") discusses how
              we will use and disclose your protected health information; you
              have received a copy of our NPP, as it is available{" "}
              <a
                href="https://www.elektrahealth.com/notice-of-privacy-practices/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black underline"
              >
                here
              </a>
              . The most up-to-date NPP is posted on our website. We will not
              disclose your health information without your consent except as
              set forth in the NPP.
            </p>
            <p className="text-gray-700">
              As set forth in the NPP, there are some situations in which we are
              legally obligated to take action and disclose your health
              information to protect yourself or others from harm, For example,
              if we become aware of child, elderly or other types of abuse or if
              a patient is threatening serious bodily harm to another, we may be
              required in certain states to disclose health information to state
              authorities or others.
            </p>
          </section>

          {/* 10. Privacy Policy */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              10. Privacy Policy
            </h2>
            <p className="text-gray-700">
              You agree to Elektra Health's{" "}
              <a
                href="https://www.elektrahealth.com/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black underline"
              >
                Privacy Policy
              </a>
              , the terms of which are incorporated herein by reference.
            </p>
          </section>

          {/* 11. Medical Records */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              11. Medical Records
            </h2>
            <p className="text-gray-700">
              You agree to the entry of your medical records into Elektra
              Health's and the Practice's computer database and understand that
              reasonable measures have been taken to safeguard your medical
              information, in accordance with federal HIPAA standards, as
              amended to date, but no computer or phone system is totally
              secure. Elektra Health recognizes your privacy and, in accordance
              with Elektra Health's Privacy Policy, will not release information
              to anyone without your written authorization or as required or
              permitted by law.
            </p>
          </section>

          {/* 12. Consent to Call, Email, Text, and Patient Portal Messaging */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              12. Consent to Call, Email, Text, and Patient Portal Messaging
            </h2>
            <p className="mb-4 text-gray-700">
              You expressly consent to allow our agents and us to communicate
              with you by telephone call, email, text message, messaging via the
              patient portal, messaging via the membership platform, and/or
              other forms of unencrypted electronic messaging ("Electronic
              Messages") using any telephone numbers or email addresses that you
              provide us or that we obtain lawfully, such as through caller ID.
              You expressly agree to receive prerecorded or automated Electronic
              Messages from us. You agree to receive these Electronic Messages
              from us even if your phone number is listed on the National Do Not
              Call Registry. You agree that you are the current owner of any
              telephone number you provide us. Wireless or data charges from
              your carrier may apply, and we are not responsible for these
              charges.
            </p>
            <ul className="mb-4 list-disc space-y-2 pl-6 text-gray-700">
              <li>
                You understand that we do not require that you give your consent
                to receive automated Electronic Messages in order to receive
                services from us.
              </li>
              <li>
                You understand that Electronic Messages sent by us may include,
                without limitation, appointment reminders, changes in previously
                scheduled appointments, actions to take in advance of
                appointments, follow-ups from appointments, information
                regarding insurance and billing, marketing material, or advice
                or education.
              </li>
              <li>
                You understand the risks associated with communicating through
                Electronic Messages, including, without limitation, that
                Electronic Messages can easily be mis-addressed to or forwarded
                to unintended recipients, that Electronic Messages can be
                stored, that backup copies of Electronic Messages may exist even
                after the Electronic Messages are deleted, that Electronic
                Messages may not be secure and thus may be used or forwarded
                without your permission or knowledge, that Electronic Messages
                may be inspected by your telephone carrier, and that Electronic
                Messages may be used as evidence in court. You understand that
                we are not liable for any breaches of confidentiality caused by
                you or a third party.
              </li>
              <li>
                You agree that in a medical emergency, you should not use
                Electronic Messages. Instead, you should call 911.
              </li>
              <li>
                You understand that Electronic Messages may be filed in your
                medical record.
              </li>
              <li>
                You may opt out of automated Electronic Messages, including
                in-app messages or notifications, SMS, and/or email, at any
                time. Your notification options and preferences are available in
                the Elektra Health patient portal. You may also send your
                request via secure message to the Elektra Health team or via
                email to care@elektrahealth.com. You acknowledge and agree to
                receive a final message confirming your choice to opt out.
                Unless you revoke your consent to communicate with us via
                Electronic Messages, your consent will last through the end of
                your relationship with us.
              </li>
              <li>
                You acknowledge that entry of any telephone contact number
                constitutes written consent to receive any automated,
                prerecorded, and artificial voice telephone calls initiated by
                the Practice. To alter or revoke this consent, visit the Patient
                Portal "Contact Preferences" page.
              </li>
              <li>
                You acknowledge that telephone calls to or from us may be
                monitored and recorded. You agree to this monitoring and
                recording.
              </li>
              <li>
                You understand that if you send us an email, the email will not
                necessarily be read or responded to by a Practitioner.
              </li>
              <li>
                In exchange for the services provided by us, you release Elektra
                Health and Elektra Health Medical Services from all claims,
                causes of action, lawsuits, damages, losses, liabilities, or
                other harms relating to any Electronic Messages you exchange
                with them. You release Elektra Health and Elektra Health Medical
                Services from all claims, causes of action, or lawsuits based on
                any alleged violations of any laws, including the Telephone
                Consumer Protection Act, the Truth in Caller ID Act, the
                CAN-SPAM Act, the Fair Debt Collection Practices Act, the Fair
                Credit Reporting Act, HIPAA, any similar state and local acts or
                statutes, and any federal or state tort or consumer protection
                laws.
              </li>
            </ul>
          </section>

          {/* 13. Acknowledgments */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              13. Acknowledgments
            </h2>
            <p className="mb-4 text-gray-700">
              You have read and understand the information provided above and
              understand and agree to the terms in this agreement, including the
              services, payment methods, and cancellation policy. Any questions
              you had have been answered. You consent to receiving telehealth
              services, as described above in Section 2. If you are receiving
              services in Connecticut, please see section 10a for additional
              disclosures regarding telehealth services. If you are receiving
              services in New Jersey, please see section 10b for additional
              disclosures regarding telehealth services.
            </p>
            <p className="mb-4 text-gray-700">
              You also understand that, under HIPAA, you have certain rights to
              privacy regarding your health information. You have received,
              read, and understand Elektra Health Medical Services' NPP
              containing a complete description of the uses and disclosures of
              your health information. You understand that Elektra Health
              Medical Services has the right to change its NPP from time to time
              and that you may contact Elektra Health Medical Services at any
              time to obtain a current copy of the NPP.
            </p>

            <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
              a. Receiving Telehealth Services in Connecticut
            </h3>
            <p className="mb-4 text-gray-700">
              If you are receiving telehealth services in Connecticut, at the
              time of your first consultation with an Elektra provider you will
              be informed about telehealth treatment methods, as well as their
              limitations. You will then be asked by the Elektra provider
              whether you consent to the provision of telehealth services, and,
              if so, asked whether you consent to Elektra sharing records
              regarding the telehealth services you receive with your primary
              care provider.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              b. Receiving Telehealth Services in Iowa
            </h3>
            <p className="mb-4 text-gray-700">
              Elektra providers will communicate with you via email as well as
              electronic messages through the Elektra platform to support
              scheduling, provision, and follow-up regarding your services. You
              can expect to receive a response from your Elektra provider within
              approximately 3 business days. For details regarding the
              information Elektra collects from you, including the use of
              passive tracking mechanisms, and your rights with respect to your
              health information, please refer to Elektra’s Privacy Policy.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              c. Receiving Telehealth Services in Missouri.
            </h3>
            <p className="mb-4 text-gray-700">
              You may receive telehealth services from a physician assistant or
              an advanced practice registered nurse. If so, you have the right
              to see the physician with whom the physician assistant or advanced
              practice registered nurse collaborates.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              d. Receiving Telehealth Services in New Jersey
            </h3>
            <p className="mb-4 text-gray-700">
              If you are receiving telehealth services in New Jersey, your
              provider's name, contact information, and license, title, and any
              specialty and board certifications will be provided to you prior
              to your telehealth visit. You will also be provided information on
              who to contact after your visit.
            </p>
            <p className="mb-4 text-gray-700">
              Your visit may occur with a professional who is not a physician –
              such as a physician assistant, nurse practitioner, or certified
              nurse midwife; if you only want to have a visit with a physician,
              please contact 646-760-6669 or email care@elektrahealth.com.
            </p>
            <p className="mb-4 text-gray-700">
              If you experience an adverse reaction to your treatment, you may
              receive follow-up care by contacting 646-760-6669.
            </p>
            <p className="mb-4 text-gray-700">
              If you cannot reach your provider prior to, during your
              appointment time or after your appointment due to technological or
              equipment difficulties, please contact 646-760-6669 or email
              care@elektrahealth.com.
            </p>
            <p className="mb-4 text-gray-700">
              You authorize Elektra to disclose medical records related to your
              telehealth encounter to your primary care provider or another
              provider that you identify upon your written request.
            </p>

            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              e. Receiving Telehealth Services in Tennessee.
            </h3>
            <p className="mb-4 text-gray-700">
              You may receive telehealth services from a physician assistant.
            </p>
          </section>
        </div>

        <div className="mt-8 rounded-lg bg-white p-6 text-center">
          <p className="text-sm text-gray-500">
            By using Elektra Health's services, you acknowledge that you have
            read and understood this Patient Services Agreement and agree to be
            bound by its terms.
          </p>
        </div>
      </div>
    </main>
  );
};
